import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField, Box, Typography, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Server } from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './IndentDemandReport.module.css';
import Header from '../../Header';
import { useNavigate } from 'react-router-dom';
import { local_signage, local_url } from '../../Const';

export const SaleOrderReportNew = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State for filters
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
  const [toDate, setToDate] = useState(dayjs());
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChannel, setSelectedChannel] = useState(''); // NEW: Channel selection (WEB or POS)
  
  // Date validation state
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);
  
  // NEW: Add State for Segments, Areas, Branches, and BranchType
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  
  // State for table
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Loading states for dropdowns
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Session timeout dialog state
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // Get user data from localStorage
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // FIXED: Helper functions to handle date/time without UTC conversion
  const parseLocalDateTime = (dateString) => {
    if (!dateString) return new Date();
    
    try {
      // Remove the 'Z' and treat as local time (IST) instead of UTC
      const localDateString = dateString.replace('Z', '');
      const date = new Date(localDateString);
      return isNaN(date.getTime()) ? new Date() : date;
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return new Date();
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDateTime(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date only:', dateString, error);
      return '';
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseLocalDateTime(dateString);
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time only:', dateString, error);
      return '';
    }
  };

  // Helper function to format closed status
  const formatClosedStatus = (closedValue) => {
    if (closedValue === true) return 'Yes';
    if (closedValue === false || closedValue === null || closedValue === undefined) return 'No';
    return 'N'; // For any other value
  };

  // Calculate TAT (Turn Around Time) in hours and minutes
  const calculateTAT = (startDateTime, endDateTime) => {
    if (!startDateTime || !endDateTime) return 'N/A';
    
    try {
      const start = parseLocalDateTime(startDateTime);
      const end = parseLocalDateTime(endDateTime);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';
      
      const diffMs = end.getTime() - start.getTime();
      
      if (diffMs < 0) return 'N/A'; // End time before start time
      
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHours === 0) {
        return `${diffMinutes}m`;
      } else {
        return `${diffHours}h ${diffMinutes}m`;
      }
    } catch (error) {
      console.error('Error calculating TAT:', startDateTime, endDateTime, error);
      return 'N/A';
    }
  };

  // Function to clear all localStorage and navigate to login
  const handleSessionTimeout = () => {
    localStorage.clear();
    setShowSessionDialog(false);
    alert('Session Timeout! Please login again.');
    navigate('/Login', { replace: true });
  };

  // Generic API call wrapper with 401 handling
  const apiCall = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 401 || response.status === 403) {
        setShowSessionDialog(true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        alert('Request timed out. Please try again.');
      }
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error('API call error:', error);
      throw error;
    }
  };

  // Session Timeout Dialog Component
  const SessionTimeoutDialog = () => (
    <Dialog
      open={showSessionDialog}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f44336', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        Session Timeout
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your session has expired
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please login again to continue using the application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={handleSessionTimeout}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Login Again
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Date picker styling
  const datePickerStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dee2e6',
      },
      '&:hover fieldset': {
        borderColor: '#adb5bd',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#6c757d',
    },
  };

  // Date picker styling with error (red border)
  const datePickerErrorStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dc3545',
        borderWidth: '2px',
      },
      '&:hover fieldset': {
        borderColor: '#c82333',
        borderWidth: '2px',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#dc3545',
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#dc3545',
    },
  };

  // Loader Component
  const FullPageLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            animation: 'spin 1s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.secondary?.main || '#1976d2',
            animation: 'spin 1.5s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.info?.main || '#0288d1',
            animation: 'spin 2s linear infinite',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '40%',
            height: '40%',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Server
            size={20}
            color={theme.palette.primary.main}
          />
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="primary"
        mb={1}
        sx={{ fontWeight: 600 }}
      >
        Loading Sales Data
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{
          maxWidth: '300px',
          animation: 'pulse 1.5s infinite ease-in-out',
        }}
      >
        Please wait while we load sales data...
      </Typography>

      <Box
        sx={{
          mt: 4,
          height: 4,
          width: 220,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `linear-gradient(90deg, 
              rgba(255,255,255,0), 
              ${theme.palette.primary.main}, 
              rgba(255,255,255,0))`,
            backgroundSize: '1000px 100%',
            animation: 'shimmer 2s infinite',
          }}
        />
      </Box>
    </Box>
  );

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' }
  ];

  // NEW: Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await apiCall(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching business segments:', error);
        }
      }
    };

    if (authToken && userId) fetchSegments();
  }, [authToken, userId]);

  // NEW: Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;

      setLoadingAreas(true);
      try {
        const response = await apiCall(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          // Add ALL option and set the areas
          const areasWithAll = [
            { SFAreaId: 'ALL', SFAreaName: 'ALL' },
            ...data
          ];
          setAreas(areasWithAll);
          setSelectedAreaObj(null);
          setBranches([]); // Clear branches when areas change
          setSelectedBranchObj(null);
          setSelectedChannel(''); // Clear channel selection when segment changes
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching areas by segment:', error);
        }
        setAreas([]);
        setBranches([]);
        setSelectedAreaObj(null);
        setSelectedBranchObj(null);
        setSelectedChannel(''); // Clear channel selection on error
      } finally {
        setLoadingAreas(false);
      }
    };

    if (authToken && branchType) fetchAreasBySegment();
  }, [branchType, authToken]);

  // NEW: Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;

      setLoadingBranches(true);
      try {
        // Use SFAreaId from the API response structure
        const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await apiCall(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBranches([{ branchID: 'ALL', branchName: 'ALL' }, ...data]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching branch list:', error);
        }
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (authToken && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, authToken]);

  // API Functions with 401 handling
  const fetchAreas = async () => {
    try {
      const response = await apiCall(`${local_url}/api/SFArea/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const areasWithAll = [
          { sfAreaId: 'ALL', sfAreaName: 'ALL' },
          ...data
        ];
        setAreas(areasWithAll);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching areas:', error);
      }
    }
  };

  const fetchBranches = async (sfAreaId) => {
    try {
      const response = await apiCall(`${local_url}/api/Company/GetBranchList/${sfAreaId}/SEGMENT/ALL`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const branchesWithAll = [
          { branchID: 'ALL', branchName: 'ALL' },
          ...data
        ];
        setBranches(branchesWithAll);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching branches:', error);
      }
    }
  };

  // Fetch sales data from the new API
  const fetchSalesData = async (areaId, branchId, fromDate, toDate, channelId = 'POS', bizSegment = 'ALL') => {
    try {
      const startDate = fromDate ? fromDate.format('YYYYMMDD') : dayjs().subtract(1, 'month').format('YYYYMMDD');
      const endDate = toDate ? toDate.format('YYYYMMDD') : dayjs().format('YYYYMMDD');

      const response = await apiCall(
        `https://tpapi.abisaio.com:3060/api/v1/reportjsonecomordermovementitemdetail?BizSegment=${bizSegment}&TransTypeID=ALL&TranSubTypeID=ALL&ChannelId=${channelId}&AreaId=${areaId}&FromDate=${startDate}&ToDate=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Raw Sales API Response:', data);

        // Handle the sales API response structure
        let actualData = data;
        if (data && data.SaleHdr && Array.isArray(data.SaleHdr)) {
          actualData = data.SaleHdr;
        } else if (Array.isArray(data)) {
          actualData = data;
        } else {
          console.error('Unexpected sales API response format:', data);
          actualData = [];
        }

        console.log('Processed sales data for transformation:', actualData);
        return transformSalesData(actualData);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch sales data:', response.status, errorText);
        alert(`Failed to fetch sales data: ${response.status} - ${errorText}`);
        return [];
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching sales data:', error);
        alert('Error fetching sales data. Please check your connection and try again.');
      }
      return [];
    }
  };







// Transform sales API data to show each sale item in separate rows
  const transformSalesData = (apiData) => {
    if (!Array.isArray(apiData)) {
      console.error('Sales API data is not an array:', apiData);
      return [];
    }

    console.log('Transforming Sales API data, count:', apiData.length);

    const transformedData = [];
    let index = 0;

    // Process each sale header
    apiData.forEach((saleHeader) => {
      try {
        // Process each SaleItem separately
        if (saleHeader.SaleItems && Array.isArray(saleHeader.SaleItems)) {
          saleHeader.SaleItems.forEach((saleItem) => {
            const transformedItem = {
              id: index + 1,
              sno: index + 1,
              saleId: saleHeader.SaleId || 'N/A',
              branchId: saleHeader.BranchId || 'N/A',
              branchName: saleHeader.BranchName || 'N/A',
              billingDateTime: formatDateOnly(saleHeader.billingDateTime),
              billingTime: formatTimeOnly(saleHeader.billingDateTime),
              channelID: saleHeader.ChannelID || 'N/A',
              customerId: saleHeader.CustomerID || 'N/A',
              customerName: saleHeader.CustomerName || 'N/A',
              mobile: saleHeader.Mobile || 'N/A',
              transTypeId: saleHeader.TransTypeId || 'N/A',
              tranSubTypeId: saleHeader.TranSubTypeId || 'N/A',
              totalAmount: saleHeader.TotalAmount || 0,
              sfAreaName: saleHeader.SFAreaName || 'N/A',
              itemId: saleItem.ItemID || 'N/A',
              itemName: saleItem.ItemName || 'N/A',
              division: saleItem.Division || 'N/A',
              itemCategoryId: saleItem.ItemCategoryID || 'N/A',
              categoryName: saleItem.CategoryName || 'N/A',
              batchNumber: saleItem.BatchNumber || 'N/A',
              qty: saleItem.Qty || 0,
              dayRate: saleItem.dayRate || 0,
              billedKg: saleItem.billedKg || 0,
              billedEa: saleItem.billedEa || 0,
              itemTotalAmount: saleItem.TotalAmount || 0,
              deliveryCharge: saleItem.DeliveryCharge || 0,
              // Payment details
              paymentMode: saleHeader.billdtl && saleHeader.billdtl.length > 0 ? saleHeader.billdtl[0].paymentGateWayId : 'N/A',
              paymentAmount: saleHeader.billdtl && saleHeader.billdtl.length > 0 ? saleHeader.billdtl[0].Amount : 0,
              // Store the raw items for detailed export
              rawSaleHeader: saleHeader,
              rawSaleItem: saleItem
            };

            transformedData.push(transformedItem);
            index++;
          });
        }
      } catch (error) {
        console.error('Error transforming sale item:', saleHeader, error);
      }
    });

    console.log(`Transformed ${transformedData.length} sale items total from ${apiData.length} sales`);
    return transformedData;
  };













  // Load areas on component mount - COMMENTED OUT as it's replaced by segment-based loading
  // useEffect(() => {
  //   if (userId && authToken) {
  //     fetchAreas();
  //   }
  // }, [userId, authToken]);

  // Load branches when area changes - COMMENTED OUT as it's replaced by segment-based loading
  // useEffect(() => {
  //   if (selectedAreaObj && selectedAreaObj.sfAreaId !== 'ALL') {
  //     fetchBranches(selectedAreaObj.sfAreaId);
  //     setSelectedBranchObj(null);
  //   } else {
  //     setBranches([]);
  //     setSelectedBranchObj(null);
  //   }
  // }, [selectedAreaObj]);

  // Initialize data
  useEffect(() => {
    setRawData([]);
    setFilteredData([]);
  }, []);

  // Date range validation - 30 days maximum
  useEffect(() => {
    if (fromDate && toDate) {
      const daysDifference = toDate.diff(fromDate, 'days');
      setIsDateRangeValid(daysDifference <= 31);
    } else {
      setIsDateRangeValid(true);
    }
  }, [fromDate, toDate]);

  // FIXED: Apply filters with better validation and error handling
  const applyFilters = async () => {
    if (!selectedAreaObj) {
      alert('Please select an Area to fetch data.');
      return;
    }

    if (!selectedChannel) {
      alert('Please select a Channel (Online Sale or Offline Sale).');
      return;
    }

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      alert('From Date cannot be after To Date.');
      return;
    }

    if (!isDateRangeValid) {
      alert('Date range cannot exceed 31 days. Please adjust the From Date to be within 31 days of the To Date.');
      return;
    }

    setLoading(true);
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);

    try {
      // Use SFAreaId from the API response structure
      const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
      let closedParam = 'Y'; // Default to closed

      if (selectedStatus === 'open') {
        closedParam = 'N';
      } else if (selectedStatus === 'closed') {
        closedParam = 'Y';
      }

      console.log('Fetching data with params:', {
        areaId,
        fromDate: fromDate.format('YYYYMMDD'),
        toDate: toDate.format('YYYYMMDD'),
        closedParam,
        channelId: selectedChannel,
        bizSegment: branchType
      });

      const data = await fetchSalesData(
        areaId,
        'ALL', // branchId is now always 'ALL'
        fromDate,
        toDate,
        selectedChannel,
        branchType
      );

      console.log('Received data:', data);
      console.log('Data length:', data.length);

      setRawData(data);
      setFilteredData(data);

      if (data.length === 0) {
        alert('No data found for the selected criteria.');
      } else {
        console.log('Successfully loaded', data.length, 'records');
        console.log('Sample record:', data[0]);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error applying filters:', error);
        alert('Error fetching data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedArea('');
    setSelectedBranch('');
    setSelectedAreaObj(null);
    setSelectedBranchObj(null);
    setBranchType(''); // NEW: Clear segment selection
    setSelectedChannel(''); // NEW: Clear channel selection
    setFromDate(dayjs().subtract(1, 'month'));
    setToDate(dayjs());
    setSelectedStatus('');
    setSearchTerm('');
    setRawData([]);
    setFilteredData([]);
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setIsDateRangeValid(true); // Reset validation state
  };

  // Export function for sales data
  const exportToXLSX = () => {
    if (filteredAndSearchedData.length === 0) {
      alert('No data to export.');
      return;
    }

    const exportData = [];

    filteredAndSearchedData.forEach(row => {
      // Create export row with sales data structure
      const exportRow = {
        'S.No': row.sno,
        'Sale ID': row.saleId,
        'Branch ID': row.branchId,
        'Branch Name': row.branchName,
        'Billing Date': row.billingDateTime,
        'Billing Time': row.billingTime,
        'Channel ID': row.channelID,
        'Customer ID': row.customerId,
        'Customer Name': row.customerName,
        'Mobile': row.mobile,
        'Transaction Type': row.transTypeId,
        'Transaction Sub Type': row.tranSubTypeId,
        'Total Amount': row.totalAmount,
        'Area Name': row.sfAreaName,
        'Item ID': row.itemId,
        'Item Name': row.itemName,
        'Division': row.division,
        'Item Category ID': row.itemCategoryId,
        'Category Name': row.categoryName,
        'Batch Number': row.batchNumber,
        'Quantity': row.qty,
        'Day Rate': row.dayRate,
        'Billed KG': row.billedKg,
        'Billed EA': row.billedEa,
        'Item Total Amount': row.itemTotalAmount,
        'Delivery Charge': row.deliveryCharge,
        'Payment Mode': row.paymentMode,
        'Payment Amount': row.paymentAmount
      };

      // Try to find and add matching order details if available
      let hasMatchingOrder = false;

      if (row.rawSaleHeader && row.rawSaleHeader.slorder && Array.isArray(row.rawSaleHeader.slorder)) {
        // Check if this item has a matching order by comparing item IDs and quantities based on UOM
        for (const order of row.rawSaleHeader.slorder) {
          if (order.orderskudtl && Array.isArray(order.orderskudtl)) {
            // Find the matching order item by ItemID and Quantity based on UOM
            const matchingOrderItem = order.orderskudtl.find(orderItem => {
              const itemIdMatch = orderItem.ItemID === row.itemId;
              if (!itemIdMatch) return false;

              // Get UOM from raw sale item
              const saleUOM = row.rawSaleItem?.UOM || '';

              // Calculate order total quantity: ItemUnitQty * Qty
              const orderTotalQty = (orderItem.ItemUnitQty || 0) * (orderItem.Qty || 0);

              // Match quantity using the multiplication formula for both KG and EA
              const qtyMatch = orderTotalQty === row.qty;

              // Also match by amount to ensure we get the correct item when same ItemID appears multiple times
              const amountMatch = Math.abs((orderItem.TotalAmount || 0) - (row.itemTotalAmount || 0)) < 0.01;

              // Match by ItemID AND Quantity AND Amount
              return itemIdMatch && qtyMatch && amountMatch;
            });

            if (matchingOrderItem) {
              hasMatchingOrder = true;
              const completeRow = {
                ...exportRow,
                'Sale Order ID': order.SaleOrderID || '',
                'Order Date': formatDateOnly(order.OrderDate),
                'Order Time': formatTimeOnly(order.OrderDate),
                'Order SKU ID': matchingOrderItem.SkuId || '',
                'Order SKU Name': matchingOrderItem.SkuName || '',
                'Order Item ID': matchingOrderItem.ItemID || '',
                'Order Item Name': matchingOrderItem.ItemName || '',
                'Order Quantity': matchingOrderItem.Qty || 0,
                'Order Item Unit Qty': matchingOrderItem.ItemUnitQty || 0,
                'Order Total Amount': matchingOrderItem.TotalAmount || 0
              };
              exportData.push(completeRow);
              break; // Exit loop after first match to avoid duplicates
            }
          }
        }
      }

      // Only add the basic row if no matching order was found
      if (!hasMatchingOrder) {
        // Add the row even if no matching order - export all items that are in SaleItems
        exportData.push(exportRow);
      }
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Order Report');
    XLSX.writeFile(wb, 'sales_order_report.xlsx');
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // FIXED: Search and sort data with better search functionality
  const filteredAndSearchedData = useMemo(() => {
    let data = filteredData;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase().trim();
      data = data.filter(item =>
        (item.saleId && item.saleId.toString().toLowerCase().includes(lower)) ||
        (item.itemName && item.itemName.toString().toLowerCase().includes(lower)) ||
        (item.categoryName && item.categoryName.toString().toLowerCase().includes(lower)) ||
        (item.itemId && item.itemId.toString().toLowerCase().includes(lower)) ||
        (item.customerId && item.customerId.toString().toLowerCase().includes(lower)) ||
        (item.customerName && item.customerName.toString().toLowerCase().includes(lower)) ||
        (item.branchId && item.branchId.toString().toLowerCase().includes(lower)) ||
        (item.branchName && item.branchName.toString().toLowerCase().includes(lower))
      );
    }

    if (!sortConfig.key) {
      return data.map((item, index) => ({
        ...item,
        sno: index + 1
      }));
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }).map((item, index) => ({
      ...item,
      sno: index + 1
    }));
  }, [filteredData, sortConfig, searchTerm]);

  // Auto-redirect to first page when search results change
  useEffect(() => {
    if (searchTerm || filteredAndSearchedData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredAndSearchedData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <>
      <Header/>
      
      <div className={styles.indent_dashboard}>
        {/* Session Timeout Dialog */}
        <SessionTimeoutDialog />

        {/* Filters Section */}
        <div className={styles.indent_filters_section}>
          <div className={styles.indent_filters_grid}>
            {/* NEW: Segment Dropdown */}
            <div className={styles.indent_filter_group}>
              <select
                value={branchType}
                onChange={e => setBranchType(e.target.value)}
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 8px',
                  minWidth: '200px',
                  color: '#495057',
                  outline: 'none',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                <option value="" style={{ color: '#6c757d' }}>Select Segment</option>
                {segments.map(segment => (
                  <option key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                    {segment.businessSegmentName}
                  </option>
                ))}
              </select>
            </div>

            {/* NEW: Channel Dropdown - Dynamic based on segment */}
            <div className={styles.indent_filter_group}>
              <select
                value={selectedChannel}
                onChange={e => setSelectedChannel(e.target.value)}
                disabled={!branchType} // Disabled until segment is selected
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: !branchType ? '#f5f5f5' : '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 8px',
                  minWidth: '200px',
                  color: '#495057',
                  outline: 'none',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                  cursor: !branchType ? 'not-allowed' : 'pointer'
                }}
                onFocus={(e) => {
                  if (branchType) {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (branchType && e.target !== document.activeElement) {
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                <option value="" style={{ color: '#6c757d' }}>Select Channel</option>
                {branchType === 'MODERN' ? (
                  <>
                    <option value="GTR">General Trading</option>
                  </>
                ) : (
                  <>
                    <option value="WEB">Online Sale</option>
                    <option value="POS">Offline Sale</option>
                  </>
                )}
              </select>
            </div>

            {/* UPDATED: Area Dropdown */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={areas}
                getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
                value={selectedAreaObj}
                onChange={(event, newValue) => {
                  setSelectedAreaObj(newValue);
                }}
                disabled={!branchType || loadingAreas} // Disabled until segment is selected or while loading
                loading={loadingAreas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingAreas ? "Loading Areas..." : "Select Area"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={!isDateRangeValid ? datePickerErrorStyle : datePickerStyle}
                  shouldDisableDate={(date) => {
                    // Only disable future dates - allow user to choose any past/present date
                    return date.isAfter(dayjs(), 'day');
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined",
                      error: !isDateRangeValid,
                      helperText: !isDateRangeValid ? 'Date range exceeds 31 days' : ''
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  format="DD-MM-YYYY"
                  sx={datePickerStyle}
                  shouldDisableDate={(date) => {
                    // Disable future dates
                    if (date.isAfter(dayjs(), 'day')) return true;
                    
                    if (!fromDate) return false;
                    // Disable dates that are before the fromDate (can't select to date before from date)
                    if (date.isBefore(fromDate, 'day')) return true;
                    // Disable dates that are more than 31 days after the fromDate
                    return date.isAfter(fromDate.add(31, 'days'));
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            {/* <div className={styles.indent_filter_group}>
              <select 
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className={styles.indent_filter_select}
              >
                <option value="">Select Status</option>
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div> */}
          </div>

          {/* Date Range Validation Message */}
          {!isDateRangeValid && fromDate && toDate && (
            <div style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #f5c6cb',
              margin: '10px 0',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontWeight: 'bold' }}>⚠️</span>
              <span>
                Date range is {toDate.diff(fromDate, 'days')} days. Maximum allowed is 31 days. 
                Please adjust the From Date to be within 31 days of the To Date.
              </span>
            </div>
          )}

          <div className={styles.indent_filter_actions}>
            <button 
              onClick={applyFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_primary}`} 
              disabled={loading || !isDateRangeValid}
              style={{
                opacity: (!isDateRangeValid || loading) ? 0.6 : 1,
                cursor: (!isDateRangeValid || loading) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Loading...' : 'Apply'}
            </button>
            <button 
              onClick={clearFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_secondary}`}
            >
              Clear
            </button>
            <button 
              onClick={exportToXLSX} 
              className={`${styles.indent_btn} ${styles.indent_btn_success}`}
            >
              Export XLSX
            </button>
            <div className={styles.indent_search_group}>
              <input
                type="text"
                placeholder="Search by Sale ID, Customer, Item..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.indent_search_input}
              />
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className={styles.indent_results_summary}>
          <span>Showing {paginatedData.length} of {filteredAndSearchedData.length} records</span>
          <div className={styles.indent_rows_per_page}>
            <label>Rows:</label>
            <select 
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.indent_rows_select}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Data Table Section */}
        <div className={styles.indent_table_section}>
          {loading ? (
            <FullPageLoader />
          ) : filteredAndSearchedData.length === 0 ? (
            <div className={styles.indent_no_data}>
              <p>No sales data found. Please select a Segment and Area, then click Apply to fetch sales data.</p>
            </div>
          ) : (
            <div className={styles.indent_table_container}>
              <table className={styles.indent_data_table}>
                <thead>
                  <tr>
                    <th className={styles.indent_non_sortable}>
                      S.No
                    </th>
                    <th onClick={() => handleSort('indentId')} className={styles.indent_sortable}>
                      Sale ID {sortConfig.key === 'saleId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('billingDateTime')} className={styles.indent_sortable}>
                      Billing Date {sortConfig.key === 'billingDateTime' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Billing Time</th>
                    <th onClick={() => handleSort('sfAreaName')} className={styles.indent_sortable}>
                      Area {sortConfig.key === 'sfAreaName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('branchId')} className={styles.indent_sortable}>
                      Branch ID {sortConfig.key === 'branchId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Branch Name</th>
                    <th>Channel ID</th>
                    <th onClick={() => handleSort('customerId')} className={styles.indent_sortable}>
                      Customer ID {sortConfig.key === 'customerId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Customer Name</th>
                    <th>Mobile</th>
                    <th>Trans Type</th>
                    <th>Trans Sub Type</th>
                    <th onClick={() => handleSort('totalAmount')} className={styles.indent_sortable}>
                      Total Amount {sortConfig.key === 'totalAmount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('itemId')} className={styles.indent_sortable}>
                      Item ID {sortConfig.key === 'itemId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Item Name</th>
                    <th>Division</th>
                    <th>Category</th>
                    <th>Batch Number</th>
                    <th onClick={() => handleSort('qty')} className={styles.indent_sortable}>
                      Quantity {sortConfig.key === 'qty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('dayRate')} className={styles.indent_sortable}>
                      Day Rate {sortConfig.key === 'dayRate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('billedKg')} className={styles.indent_sortable}>
                      Billed KG {sortConfig.key === 'billedKg' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('billedEa')} className={styles.indent_sortable}>
                      Billed EA {sortConfig.key === 'billedEa' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('itemTotalAmount')} className={styles.indent_sortable}>
                      Item Amount {sortConfig.key === 'itemTotalAmount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Delivery Charge</th>
                    <th>Payment Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td>{row.sno}</td>
                      <td className={styles.indent_ticket_id}>{row.saleId}</td>
                      <td>{row.billingDateTime}</td>
                      <td>{row.billingTime}</td>
                      <td className={styles.indent_capitalize}>{row.sfAreaName}</td>
                      <td className={styles.indent_ticket_id}>{row.branchId}</td>
                      <td>{row.branchName}</td>
                      <td>{row.channelID}</td>
                      <td className={styles.indent_ticket_id}>{row.customerId}</td>
                      <td>{row.customerName}</td>
                      <td>{row.mobile}</td>
                      <td>{row.transTypeId}</td>
                      <td>{row.tranSubTypeId}</td>
                      <td className={styles.indent_amount}>{Number(row.totalAmount).toFixed(2)}</td>
                      <td className={styles.indent_ticket_id}>{row.itemId}</td>
                      <td>{row.itemName}</td>
                      <td>{row.division}</td>
                      <td>{row.categoryName}</td>
                      <td>{row.batchNumber}</td>
                      <td className={styles.indent_amount}>{row.qty}</td>
                      <td className={styles.indent_amount}>{Number(row.dayRate).toFixed(2)}</td>
                      <td className={styles.indent_amount}>{Number(row.billedKg).toFixed(3)}</td>
                      <td className={styles.indent_amount}>{row.billedEa}</td>
                      <td className={styles.indent_amount}>{Number(row.itemTotalAmount).toFixed(2)}</td>
                      <td className={styles.indent_amount}>{Number(row.deliveryCharge).toFixed(2)}</td>
                      <td>{row.paymentMode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredAndSearchedData.length > 0 && !loading && (
          <div className={styles.indent_pagination}>
            {/* First Page Button */}
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              First
            </button>
            
            {/* Previous Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Prev
            </button>
            
            {/* Page Numbers */}
            <div className={styles.indent_page_numbers}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`${styles.indent_btn} ${styles.indent_btn_pagination} ${currentPage === page ? styles.indent_active : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Next
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
};

export default SaleOrderReportNew;
